import { Fragment, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import {
  Card,
  ErrorBox,
  ManagerLink,
  PageHeader,
  Spinner,
  StatusBadge,
  TeamTag,
} from "../components/ui.js";
import StatLineTable from "../components/StatLineTable.js";
import { STAGE_LABEL, formatDateTime, gameSlot } from "../lib/format.js";
import { useSession } from "../lib/session.js";
import { periodLabel, sumPeriods, type LineScore } from "@mpl/shared";

export default function GameDetail() {
  const { id } = useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { user, isCommissioner } = useSession();
  const query = useQuery(trpc.games.byId.queryOptions({ id: Number(id) }));
  const [confirmReopen, setConfirmReopen] = useState(false);

  const reopen = useMutation(
    trpc.games.reopen.mutationOptions({
      onSuccess: () => {
        setConfirmReopen(false);
        queryClient.invalidateQueries({ queryKey: trpc.games.pathKey() });
        queryClient.invalidateQueries({ queryKey: trpc.league.pathKey() });
      },
    }),
  );

  if (query.isLoading) return <Spinner />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!query.data) return null;

  const g = query.data;
  const isFinal = g.status === "final";
  // NFL rules, so a game can finish level. Say so rather than printing "Final"
  // over two identical numbers and leaving the reader to work it out.
  const tied =
    isFinal && g.home.score != null && g.home.score === g.away.score;
  const canEdit =
    user &&
    (user.role === "commissioner" ||
      user.id === g.home.managerId ||
      user.id === g.away.managerId);

  const nameOf = (id: number | null | undefined) =>
    id == null
      ? null
      : id === g.home.managerId
        ? g.home.managerName
        : id === g.away.managerId
          ? g.away.managerName
          : "the Commissioner";

  // Every submission typed by someone other than the manager it belongs to
  // means the result was recorded on their behalf rather than reported.
  const recordedByOther =
    g.submissions.length > 0 &&
    g.submissions.every((s) => s.enteredById != null && s.enteredById !== s.managerId)
      ? g.submissions[0].enteredById
      : null;

  const columns = [g.away, g.home]
    .filter((s) => s.managerId != null)
    .map((s) => {
      const enteredBy = g.statsEnteredBy[s.managerId!];
      // Say so when a line was typed by someone other than the manager it
      // belongs to — it's a weaker source than their own entry.
      const byOther = enteredBy != null && enteredBy !== s.managerId;
      return {
        key: String(s.managerId),
        label: byOther
          ? `${s.managerName ?? "—"} (entered by ${nameOf(enteredBy)})`
          : (s.managerName ?? "—"),
        line: g.stats[s.managerId!] ?? {},
      };
    });

  return (
    <div>
      <PageHeader
        title={gameSlot(g)}
        subtitle={STAGE_LABEL[g.stage] ?? g.stage}
        action={
          canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/submit/${g.id}`} className="btn-secondary">
                {isFinal ? "Edit result" : "Enter result"}
              </Link>
              {/* The only place a confirmed result can be sent back for
                  re-entry. Managers are told to ask the Commissioner to reopen
                  a locked game, so the Commissioner needs somewhere to do it. */}
              {isCommissioner && isFinal && (
                <button
                  className="btn-ghost"
                  onClick={() => setConfirmReopen(true)}
                  title="Send this back for re-entry"
                >
                  Reopen
                </button>
              )}
            </div>
          )
        }
      />

      {confirmReopen && (
        <Card title="Reopen this game?" className="mb-6">
          <div className="space-y-3 px-6 py-4 text-sm text-chalk-300">
            <p>
              This clears the {g.away.score}–{g.home.score} score and both
              managers' reports, and puts the game back to Scheduled so it can
              be entered again. Stat lines already recorded are kept.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                className="btn-danger"
                disabled={reopen.isPending}
                onClick={() => reopen.mutate({ gameId: g.id })}
              >
                {reopen.isPending ? "Reopening…" : "Yes, reopen it"}
              </button>
              <button className="btn-ghost" onClick={() => setConfirmReopen(false)}>
                Cancel
              </button>
            </div>
            {reopen.error && <ErrorBox error={reopen.error} />}
          </div>
        </Card>
      )}

      <Card>
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 p-6">
          {/* The key belongs on the element the map returns — a bare fragment
              takes none, so React warns and may reuse the wrong side's node. */}
          {[g.away, g.home].map((side, idx) => (
            <Fragment key={side.managerId ?? idx}>
              {idx === 1 && (
                <div className="text-center">
                  <div className="text-xs uppercase tracking-wider text-chalk-500">
                    {isFinal ? (tied ? "Tie" : "Final") : "vs"}
                  </div>
                  <div className="mt-1">
                    <StatusBadge status={g.status} />
                  </div>
                </div>
              )}
              <div className={idx === 0 ? "text-left" : "text-right"}>
                <div className="text-[11px] uppercase tracking-wider text-chalk-500">
                  {idx === 0 ? "Away" : "Home"}
                </div>
                <div className="mt-1 text-xl font-bold">
                  <ManagerLink
                    slug={side.managerSlug}
                    name={side.managerName ?? side.placeholder ?? "TBD"}
                  />
                </div>
                <div className="mt-1.5">
                  <TeamTag abbr={side.teamAbbr} />
                  {side.teamName && (
                    <span className="ml-2 text-xs text-chalk-500">
                      {side.teamName}
                    </span>
                  )}
                </div>
                <div className="mt-2 font-mono text-4xl font-bold tabular-nums">
                  {side.score ?? "—"}
                </div>
              </div>
            </Fragment>
          ))}
        </div>

        {g.lineScore && (
          <LineScoreTable
            line={g.lineScore}
            awayName={g.away.managerName}
            homeName={g.home.managerName}
          />
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-field-800 px-6 py-3 text-xs text-chalk-500">
          {g.playedAt && <span>Played {formatDateTime(g.playedAt)}</span>}
          {!g.playedAt && g.scheduledFor && (
            <span>Scheduled {formatDateTime(g.scheduledFor)}</span>
          )}
          {g.matchupType && <span>{g.matchupType} matchup</span>}
          {/* A result both managers reported is a stronger record than one the
              Commissioner typed for them, so the page says which it was. */}
          {recordedByOther && <span>Recorded by {nameOf(recordedByOther)}</span>}
        </div>

        {/* Three notes, three authors, none of them able to write over another:
            each manager's own, filed with their half of the result, and the
            Commissioner's on the game itself. */}
        {(g.notes || g.submissions.some((s) => s.notes)) && (
          <div className="space-y-3 border-t border-field-800 px-6 py-3">
            {g.submissions
              .filter((s) => s.notes)
              .map((s) => (
                <div key={s.managerId}>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-chalk-500">
                    {s.managerName}
                  </div>
                  <p className="whitespace-pre-line text-sm text-chalk-400">
                    {s.notes}
                  </p>
                </div>
              ))}
            {g.notes && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-chalk-500">
                  Commissioner
                </div>
                <p className="whitespace-pre-line text-sm text-chalk-400">{g.notes}</p>
              </div>
            )}
          </div>
        )}
      </Card>

      {g.scoresDisagree && (
        <Card title="Reported scores disagree" className="mt-6">
          <div className="space-y-2 px-6 py-4 text-sm">
            <p className="text-chalk-400">
              Both managers put a number against the same side and the two
              don't match, so the game isn't final. The Commissioner settles it.
            </p>
            <ul className="space-y-1">
              {g.submissions.map((s) => (
                <li key={s.managerId} className="font-mono text-chalk-200">
                  {s.managerName}: {g.away.managerName} {s.awayScore ?? "—"} –{" "}
                  {s.homeScore ?? "—"} {g.home.managerName}
                </li>
              ))}
            </ul>
            <p className="text-xs text-chalk-500">
              A dash is a side that report didn't cover.
            </p>
          </div>
        </Card>
      )}

      {(g.bans.length > 0 || g.firstPickManagerId != null) && (
        <Card title="Bans and picks" className="mt-6">
          <div className="space-y-3 px-6 py-4 text-sm">
            {g.firstPickManagerId != null && (
              <p className="text-chalk-400">
                <span className="text-chalk-200">
                  {g.firstPickManagerId === g.home.managerId
                    ? g.home.managerName
                    : g.away.managerName}
                </span>{" "}
                picked first.
              </p>
            )}
            {g.bans.length > 0 && (
              <ol className="space-y-1">
                {g.bans.map((b) => (
                  <li key={b.banOrder} className="flex items-center gap-2">
                    <span className="w-5 text-xs text-chalk-600">{b.banOrder}.</span>
                    {/* Who banned derives from the pick order, so it's blank
                        when that wasn't recorded. */}
                    <span className="text-chalk-300">
                      {b.managerName ?? "Banned"}
                    </span>
                    {b.managerName && <span className="text-chalk-600">banned</span>}
                    <TeamTag abbr={b.teamAbbr} />
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Card>
      )}

      {columns.length > 0 && (
        <Card title="Stat lines" className="mt-6">
          <StatLineTable columns={columns} />
          <p className="border-t border-field-800 px-4 py-3 text-xs text-chalk-500">
            Sacks taken and interceptions thrown come from the opposing
            defence's own line, so both sides tell the same story.
          </p>
        </Card>
      )}
    </div>
  );
}

/**
 * The quarter-by-quarter line score, laid out the way a box score is: periods
 * across, teams down, the total on the right. Only rendered when the breakdown
 * was recorded — plenty of games only ever had a final score reported.
 */
function LineScoreTable({
  line,
  awayName,
  homeName,
}: {
  line: LineScore;
  awayName: string | null;
  homeName: string | null;
}) {
  const rows = [
    { key: "away", name: awayName ?? "Away", periods: line.away },
    { key: "home", name: homeName ?? "Home", periods: line.home },
  ];

  return (
    <div className="overflow-x-auto border-t border-field-800 px-6 py-4">
      <table className="w-full min-w-[280px]">
        <thead>
          <tr>
            <th className="th pl-0 text-left">Scoring</th>
            {line.home.map((_, i) => (
              <th key={i} className="th w-10 text-right">
                {periodLabel(i)}
              </th>
            ))}
            <th className="th w-12 text-right">T</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t border-field-850">
              <td className="py-1.5 pr-2 text-sm text-chalk-300">{row.name}</td>
              {row.periods.map((points, i) => (
                <td
                  key={i}
                  className="py-1.5 pl-2 text-right font-mono text-sm tabular-nums text-chalk-200"
                >
                  {points}
                </td>
              ))}
              <td className="py-1.5 pl-2 text-right font-mono text-sm font-bold tabular-nums">
                {sumPeriods(row.periods)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
